import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { updateTemplate } from '../../api/discord';
import type { DiscordWebhook, DiscordTemplate } from '../../types/discord';
import { TRIGGER_LABELS } from '../../types/discord';
import { extractVariables } from './templateUtils';

interface Props {
  open: boolean;
  template: DiscordTemplate;
  webhooks: DiscordWebhook[];
  onClose: () => void;
  onSaved: (tpl: DiscordTemplate) => void;
}

export function EditTemplateModal({ open, template, webhooks, onClose, onSaved }: Props) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('');
  const [webhookId, setWebhookId] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [active, setActive] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(template.name);
      setTrigger(template.trigger);
      setWebhookId(template.webhookId);
      setDescription(template.description);
      setBody(template.template);
      setActive(template.active);
      setError('');
    }
  }, [open, template]);

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      const tpl = await updateTemplate(template.id, {
        name,
        trigger,
        webhookId,
        description,
        template: body,
        active,
      });
      onSaved(tpl);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }

  const variables = extractVariables(body);
  const valid = name.trim() && trigger && webhookId && body.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Notification Template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Trigger</InputLabel>
            <Select value={trigger} label="Trigger" onChange={(e) => setTrigger(e.target.value)}>
              {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Webhook</InputLabel>
            <Select value={webhookId} label="Webhook" onChange={(e) => setWebhookId(e.target.value)}>
              {webhooks.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name} · {w.channel}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
          />
          <TextField
            label="Message template"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={3}
            size="small"
            helperText="Use {{variable_name}} placeholders"
          />
          {variables.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Variables detected:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {variables.map((v) => (
                  <Chip key={v} label={`{{${v}}}`} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
          {error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
